import { API_BASE } from '@/shared/services/api';
/**
 * Trail Event Service
 * Centralized service for emitting trail events from frontend applications
 * Follows EVENT_CATALOG.md specifications
 */

const API_URL = API_BASE;

// Trail Types
export const TrailType = {
  AUDIT: 'audit',
  OPERATIONAL: 'operational',
  SCAN: 'scan',
  PROOF: 'proof'
};

// Actor Types
export const ActorType = {
  USER: 'USER',
  SYSTEM: 'SYSTEM',
  INTEGRATION: 'INTEGRATION'
};

// Barcode Types
export const BarcodeType = {
  SKU: 'SKU',
  CARTON: 'CARTON',
  PALLET: 'PALLET',
  LOCATION: 'LOCATION',
  LABEL: 'LABEL',
  NFE: 'NFE',
  CTE: 'CTE',
  ORDER: 'ORDER',
  ROUTE: 'ROUTE'
};

// Scan Contexts
export const ScanContext = {
  RECEIVING: 'RECEIVING',
  PUTAWAY: 'PUTAWAY',
  PICKING: 'PICKING',
  PACKING: 'PACKING',
  LOADING: 'LOADING',
  DELIVERY: 'DELIVERY',
  RETURN: 'RETURN'
};

// Photo Types
export const PhotoType = {
  DELIVERY_LOCATION: 'DELIVERY_LOCATION',
  PACKAGE: 'PACKAGE',
  DAMAGE: 'DAMAGE',
  RECIPIENT: 'RECIPIENT',
  FAILED_ATTEMPT: 'FAILED_ATTEMPT'
};

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('ezerpToken');
};

/**
 * Get current device info
 */
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  return {
    user_agent: ua,
    platform: navigator.platform,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`
  };
};

/**
 * Get current geo position (if available)
 */
export const getCurrentPosition = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          captured_at: new Date().toISOString()
        });
      },
      () => {
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 5000 }
    );
  });
};

/**
 * Emit a trail event
 * @param {Object} eventData - Event data following TrailEventCreate schema
 * @returns {Promise<Object>} - Response with event_id and correlation_id
 */
export const emitTrailEvent = async (eventData) => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.warn('[TrailService] No auth token, skipping event emission');
      return null;
    }

    const response = await fetch(`${API_URL}/api/trails/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(eventData)
    });

    if (!response.ok) {
      console.error('[TrailService] Failed to emit event:', response.status);
      return null;
    }

    const result = await response.json();
    console.log('[TrailService] Event emitted:', eventData.event_type, result.event_id);
    return result;
  } catch (error) {
    console.error('[TrailService] Error emitting event:', error);
    return null;
  }
};

/**
 * Validate a scan at a gate
 * @param {Object} scanData - Scan data following ScanEventCreate schema
 * @returns {Promise<Object>} - Scan validation result
 */
export const validateScan = async (scanData) => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.warn('[TrailService] No auth token, skipping scan validation');
      return { result: 'WARNING', passed: true, message: 'Offline mode' };
    }

    const params = new URLSearchParams({
      gate_id: scanData.gate_id,
      barcode_value: scanData.barcode_value,
      barcode_type: scanData.barcode_type,
      scan_context: scanData.scan_context,
      ...(scanData.chain_item_id && { chain_item_id: scanData.chain_item_id }),
      ...(scanData.chain_carton_id && { chain_carton_id: scanData.chain_carton_id }),
      ...(scanData.chain_pallet_id && { chain_pallet_id: scanData.chain_pallet_id }),
      ...(scanData.chain_route_id && { chain_route_id: scanData.chain_route_id }),
      ...(scanData.chain_stop_id && { chain_stop_id: scanData.chain_stop_id }),
      ...(scanData.geo_lat && { geo_lat: scanData.geo_lat }),
      ...(scanData.geo_lng && { geo_lng: scanData.geo_lng })
    });

    const response = await fetch(`${API_URL}/api/trails/scan/validate?${params}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      console.error('[TrailService] Scan validation failed:', response.status);
      return { result: 'WARNING', passed: true, message: 'Validation unavailable' };
    }

    return await response.json();
  } catch (error) {
    console.error('[TrailService] Error validating scan:', error);
    return { result: 'WARNING', passed: true, message: 'Offline mode' };
  }
};

/**
 * Create a POD record
 * @param {Object} podData - POD data following PODCreate schema
 * @returns {Promise<Object>} - Created POD record
 */
export const createPOD = async (podData) => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.warn('[TrailService] No auth token, skipping POD creation');
      return null;
    }

    const response = await fetch(`${API_URL}/api/trails/pod`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(podData)
    });

    if (!response.ok) {
      console.error('[TrailService] Failed to create POD:', response.status);
      return null;
    }

    const result = await response.json();
    console.log('[TrailService] POD created:', result.pod_id);
    return result;
  } catch (error) {
    console.error('[TrailService] Error creating POD:', error);
    return null;
  }
};

// ============== WMS EVENTS (EzERP Mobile) ==============

/**
 * Emit WMS Receiving events
 */
export const wmsEvents = {
  // Receiving
  receivingAsnLoaded: (asnId, nfeKey, userId) => emitTrailEvent({
    event_type: 'WMS.RECEIVING.ASN_LOADED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_receiving' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'SHIPMENT',
    entity_id: asnId,
    payload: { asn_id: asnId, nfe_key: nfeKey }
  }),

  receivingStarted: (receivingId, asnId, userId) => emitTrailEvent({
    event_type: 'WMS.RECEIVING.STARTED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_receiving' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'SHIPMENT',
    entity_id: receivingId,
    shipment_id: asnId,
    payload: { receiving_id: receivingId, asn_id: asnId }
  }),

  receivingItemScanned: (receivingId, item, scanResult, userId) => emitTrailEvent({
    event_type: 'WMS.RECEIVING.ITEM_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_receiving' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ITEM',
    entity_id: item.sku,
    shipment_id: receivingId,
    payload: {
      receiving_id: receivingId,
      item: {
        sku: item.sku,
        gtin: item.gtin,
        lot: item.lot,
        qty_scanned: item.qty_scanned
      },
      scan_result: scanResult
    }
  }),

  receivingCompleted: (receivingId, stats, userId) => emitTrailEvent({
    event_type: 'WMS.RECEIVING.COMPLETED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_receiving' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'SHIPMENT',
    entity_id: receivingId,
    payload: { receiving_id: receivingId, stats }
  }),

  // Put-away
  putawayTaskStarted: (taskId, userId) => emitTrailEvent({
    event_type: 'WMS.PUTAWAY.TASK_STARTED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_putaway' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'TASK',
    entity_id: taskId,
    payload: { task_id: taskId }
  }),

  putawayPalletScanned: (taskId, palletId, userId) => emitTrailEvent({
    event_type: 'WMS.PUTAWAY.PALLET_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_putaway' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'PALLET',
    entity_id: palletId,
    payload: { task_id: taskId, pallet_id: palletId }
  }),

  putawayLocationScanned: (taskId, location, userId) => emitTrailEvent({
    event_type: 'WMS.PUTAWAY.LOCATION_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_putaway' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'LOCATION',
    entity_id: location,
    payload: { task_id: taskId, location }
  }),

  putawayCompleted: (taskId, location, userId) => emitTrailEvent({
    event_type: 'WMS.PUTAWAY.COMPLETED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_putaway' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'TASK',
    entity_id: taskId,
    payload: { task_id: taskId, location }
  }),

  // Picking
  pickingTaskStarted: (orderId, userId) => emitTrailEvent({
    event_type: 'WMS.PICKING.TASK_STARTED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_picking' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ORDER',
    entity_id: orderId,
    order_id: orderId,
    payload: { order_id: orderId }
  }),

  pickingSlipScanned: (orderId, userId) => emitTrailEvent({
    event_type: 'WMS.PICKING.SLIP_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_picking' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ORDER',
    entity_id: orderId,
    order_id: orderId,
    payload: { order_id: orderId }
  }),

  pickingLocationScanned: (orderId, location, userId) => emitTrailEvent({
    event_type: 'WMS.PICKING.LOCATION_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_picking' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'LOCATION',
    entity_id: location,
    order_id: orderId,
    payload: { order_id: orderId, location }
  }),

  pickingItemScanned: (orderId, item, userId) => emitTrailEvent({
    event_type: 'WMS.PICKING.ITEM_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_picking' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ITEM',
    entity_id: item.sku,
    order_id: orderId,
    payload: { order_id: orderId, item }
  }),

  pickingCompleted: (orderId, stats, userId) => emitTrailEvent({
    event_type: 'WMS.PICKING.TASK_COMPLETED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_picking' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ORDER',
    entity_id: orderId,
    order_id: orderId,
    payload: { order_id: orderId, stats }
  }),

  // Packing
  packingSessionStarted: (orderId, userId) => emitTrailEvent({
    event_type: 'WMS.PACKING.SESSION_STARTED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_packing' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ORDER',
    entity_id: orderId,
    order_id: orderId,
    payload: { order_id: orderId }
  }),

  packingOrderScanned: (orderId, userId) => emitTrailEvent({
    event_type: 'WMS.PACKING.ORDER_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_packing' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ORDER',
    entity_id: orderId,
    order_id: orderId,
    payload: { order_id: orderId }
  }),

  packingItemScanned: (orderId, cartonId, item, userId) => emitTrailEvent({
    event_type: 'WMS.PACKING.ITEM_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_packing' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ITEM',
    entity_id: item.sku,
    order_id: orderId,
    payload: { order_id: orderId, carton_id: cartonId, item }
  }),

  packingCartonSealed: (orderId, cartonId, userId) => emitTrailEvent({
    event_type: 'WMS.PACKING.CARTON_SEALED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_packing' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'CARTON',
    entity_id: cartonId,
    order_id: orderId,
    payload: { order_id: orderId, carton_id: cartonId }
  }),

  packingCompleted: (orderId, cartonCount, userId) => emitTrailEvent({
    event_type: 'WMS.PACKING.COMPLETED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_packing' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ORDER',
    entity_id: orderId,
    order_id: orderId,
    payload: { order_id: orderId, carton_count: cartonCount }
  }),

  // Load-out
  loadoutRouteSelected: (routeId, userId) => emitTrailEvent({
    event_type: 'WMS.LOADOUT.ROUTE_SELECTED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_loadout' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ROUTE',
    entity_id: routeId,
    route_id: routeId,
    payload: { route_id: routeId }
  }),

  loadoutItemScanned: (routeId, itemId, itemType, userId) => emitTrailEvent({
    event_type: 'WMS.LOADOUT.PALLET_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'WMS', service: 'mobile_loadout' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: itemType.toUpperCase(),
    entity_id: itemId,
    route_id: routeId,
    payload: { route_id: routeId, item_id: itemId, item_type: itemType }
  }),

  loadoutRouteLoaded: (routeId, packageCount, userId) => emitTrailEvent({
    event_type: 'WMS.LOADOUT.ROUTE_LOADED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'WMS', service: 'mobile_loadout' },
    actor: { id: userId, type: ActorType.USER },
    entity_type: 'ROUTE',
    entity_id: routeId,
    route_id: routeId,
    payload: { route_id: routeId, package_count: packageCount }
  })
};

// ============== PARCEL EVENTS (EzParcel Driver) ==============

export const parcelEvents = {
  // Route Loading
  routePackageScanned: (routeId, packageId, driverId) => emitTrailEvent({
    event_type: 'PARCEL.PACKAGE.LOADED',
    trail_type: TrailType.SCAN,
    source: { module: 'PARCEL', service: 'driver_app' },
    actor: { id: driverId, type: ActorType.USER },
    entity_type: 'PACKAGE',
    entity_id: packageId,
    route_id: routeId,
    package_id: packageId,
    payload: { route_id: routeId, package_id: packageId }
  }),

  routeLoaded: (routeId, packageIds, driverId) => emitTrailEvent({
    event_type: 'PARCEL.ROUTE.LOADED',
    trail_type: TrailType.OPERATIONAL,
    source: { module: 'PARCEL', service: 'driver_app' },
    actor: { id: driverId, type: ActorType.USER },
    entity_type: 'ROUTE',
    entity_id: routeId,
    route_id: routeId,
    payload: { route_id: routeId, package_count: packageIds.length, package_ids: packageIds }
  }),

  routeStarted: async (routeId, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.ROUTE.STARTED',
      trail_type: TrailType.OPERATIONAL,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'ROUTE',
      entity_id: routeId,
      route_id: routeId,
      payload: { 
        route_id: routeId, 
        started_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  routeCompleted: async (routeId, stats, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.ROUTE.COMPLETED',
      trail_type: TrailType.OPERATIONAL,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'ROUTE',
      entity_id: routeId,
      route_id: routeId,
      payload: { 
        route_id: routeId, 
        stats,
        completed_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  // Stop Events
  stopEnRoute: async (routeId, stopId, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.STOP.EN_ROUTE',
      trail_type: TrailType.OPERATIONAL,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'STOP',
      entity_id: stopId,
      route_id: routeId,
      stop_id: stopId,
      payload: { route_id: routeId, stop_id: stopId, geo_stamp: geo }
    });
  },

  stopArrived: async (routeId, stopId, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.STOP.ARRIVED',
      trail_type: TrailType.OPERATIONAL,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'STOP',
      entity_id: stopId,
      route_id: routeId,
      stop_id: stopId,
      payload: { 
        route_id: routeId, 
        stop_id: stopId, 
        arrived_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  stopPackageScanned: (routeId, stopId, packageId, driverId) => emitTrailEvent({
    event_type: 'PARCEL.STOP.PACKAGE_SCANNED',
    trail_type: TrailType.SCAN,
    source: { module: 'PARCEL', service: 'driver_app' },
    actor: { id: driverId, type: ActorType.USER },
    entity_type: 'PACKAGE',
    entity_id: packageId,
    route_id: routeId,
    stop_id: stopId,
    package_id: packageId,
    payload: { route_id: routeId, stop_id: stopId, package_id: packageId }
  }),

  stopDelivered: async (routeId, stopId, packageIds, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.STOP.DELIVERED',
      trail_type: TrailType.OPERATIONAL,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'STOP',
      entity_id: stopId,
      route_id: routeId,
      stop_id: stopId,
      payload: { 
        route_id: routeId, 
        stop_id: stopId, 
        package_ids: packageIds,
        delivered_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  stopFailed: async (routeId, stopId, failureReason, failureDetail, contactAttempts, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.STOP.FAILED',
      trail_type: TrailType.OPERATIONAL,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'STOP',
      entity_id: stopId,
      route_id: routeId,
      stop_id: stopId,
      payload: { 
        route_id: routeId, 
        stop_id: stopId, 
        failure_reason: failureReason,
        failure_detail: failureDetail,
        contact_attempts: contactAttempts,
        failed_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  // POD Events
  podSignatureCaptured: async (routeId, stopId, shipmentId, recipientName, signatureData, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.POD.SIGNATURE_CAPTURED',
      trail_type: TrailType.PROOF,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'SHIPMENT',
      entity_id: shipmentId,
      route_id: routeId,
      stop_id: stopId,
      shipment_id: shipmentId,
      payload: { 
        route_id: routeId, 
        stop_id: stopId, 
        shipment_id: shipmentId,
        recipient_name: recipientName,
        signature_data: signatureData,
        captured_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  podPhotoCaptured: async (routeId, stopId, shipmentId, photoData, photoType, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.POD.PHOTO_CAPTURED',
      trail_type: TrailType.PROOF,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'SHIPMENT',
      entity_id: shipmentId,
      route_id: routeId,
      stop_id: stopId,
      shipment_id: shipmentId,
      payload: { 
        route_id: routeId, 
        stop_id: stopId, 
        shipment_id: shipmentId,
        photo_data: photoData,
        photo_type: photoType,
        captured_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  podCompleted: async (routeId, stopId, shipmentId, packageIds, podLevel, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.POD.COMPLETED',
      trail_type: TrailType.PROOF,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'SHIPMENT',
      entity_id: shipmentId,
      route_id: routeId,
      stop_id: stopId,
      shipment_id: shipmentId,
      payload: { 
        route_id: routeId, 
        stop_id: stopId, 
        shipment_id: shipmentId,
        package_ids: packageIds,
        pod_level: podLevel,
        completed_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  // Return Events
  packageReturned: async (routeId, packageId, returnReason, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.PACKAGE.RETURNED',
      trail_type: TrailType.OPERATIONAL,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'PACKAGE',
      entity_id: packageId,
      route_id: routeId,
      package_id: packageId,
      payload: { 
        route_id: routeId, 
        package_id: packageId,
        return_reason: returnReason,
        returned_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  // OTP Verification Event
  podOTPVerified: async (routeId, stopId, shipmentId, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.POD.OTP_VERIFIED',
      trail_type: TrailType.PROOF,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'SHIPMENT',
      entity_id: shipmentId,
      route_id: routeId,
      stop_id: stopId,
      shipment_id: shipmentId,
      payload: { 
        route_id: routeId, 
        stop_id: stopId, 
        shipment_id: shipmentId,
        otp_verified: true,
        verified_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  },

  // ID Verification Event
  podIDVerified: async (routeId, stopId, shipmentId, idType, idLast4, driverId) => {
    const geo = await getCurrentPosition();
    return emitTrailEvent({
      event_type: 'PARCEL.POD.ID_VERIFIED',
      trail_type: TrailType.PROOF,
      source: { module: 'PARCEL', service: 'driver_app' },
      actor: { id: driverId, type: ActorType.USER },
      entity_type: 'SHIPMENT',
      entity_id: shipmentId,
      route_id: routeId,
      stop_id: stopId,
      shipment_id: shipmentId,
      payload: { 
        route_id: routeId, 
        stop_id: stopId, 
        shipment_id: shipmentId,
        id_type: idType,
        id_last4: idLast4,
        id_verified: true,
        verified_at: new Date().toISOString(),
        geo_stamp: geo
      }
    });
  }
};

export default {
  emitTrailEvent,
  validateScan,
  createPOD,
  getCurrentPosition,
  wmsEvents,
  parcelEvents,
  TrailType,
  ActorType,
  BarcodeType,
  ScanContext,
  PhotoType
};
